import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getVehiclesSuggestions,
  clearSuggestions,
} from "../../store/slices/vehiclesSlice";
import { VehicleSuggestion } from "../../types/vehicles";
import { Truck, Hash } from "lucide-react";
import { Vehicle } from "../../types/vehicles";
import { useTranslation } from "react-i18next";
import { vehicleLabel, vehiclePlateHint } from "../../utils/vehicleLabel";

/*  TYPES */
type SearchField = "truckLicensePlate" | "trailerLicensePlate";

interface CarrierAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onCarrierSelect: (carrier: VehicleSuggestion) => void;
  searchField?: SearchField;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  vehicles: Vehicle[];
  size?: "sm" | "md";
}

/*  ICON MAP – choose the leading icon for each search field */
const iconForField: Record<SearchField, React.ElementType> = {
  truckLicensePlate: Truck,
  trailerLicensePlate: Hash,
};

/*  MAIN COMPONENT */
export const CarrierAutocomplete: React.FC<CarrierAutocompleteProps> = ({
  value,
  onChange,
  onCarrierSelect,
  searchField = "truckLicensePlate",
  placeholder = "Enter Truck License Plate",
  disabled = false,
  error = false,
  className = "",
  size = "md",
}) => {
  const { t } = useTranslation("shipments");

  const dispatch = useAppDispatch();
  const { suggestions, loading } = useAppSelector((state) => state.vehicles);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value);
  const [localLoading, setLocalLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const hasActiveSearch = useRef(false);

  /*  Debounced search */
  const debouncedSearch = useCallback(
    (term: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        if (term.trim().length >= 2) {
          let type = "TRUCK";
          if (searchField != "truckLicensePlate") {
            type = "TRAILER";
          }
          hasActiveSearch.current = true;
          setLocalLoading(true);
          dispatch(
            getVehiclesSuggestions({
              query: term.trim(),
              limit: 10,
              type: type,
            }),
          );
          setIsOpen(true);
        } else {
          dispatch(clearSuggestions());
          setIsOpen(false);
        }
      }, 750);
    },
    [dispatch, searchField],
  );

  /*  Handlers */

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    setSearchTerm(newVal);
    onChange(newVal);
    debouncedSearch(newVal);
  };

  const handleCarrierSelect = (carrier: VehicleSuggestion) => {
    // A vehicle carries one plate — `registrationNumber`. `searchField` says
    // which *kind* of vehicle was being searched for (truck or trailer), and
    // indexing the record by it found nothing: every consumer was handed
    // `undefined` on select, which left this input uncontrolled and crashed any
    // caller that treated the value as a string. Shown by its display name
    // when it has one; the id `onCarrierSelect` stores is what is saved.
    const selectedVal = vehicleLabel(carrier);
    setSearchTerm(selectedVal);
    onChange(selectedVal);
    onCarrierSelect(carrier);
    setIsOpen(false);
    dispatch(clearSuggestions());
  };

  /* Reset local loading when the shared Redux request settles */
  useEffect(() => {
    if (!loading && hasActiveSearch.current) {
      setLocalLoading(false);
      hasActiveSearch.current = false;
    }
  }, [loading]);

  /* Click‑outside to close */
  useEffect(() => {
    const listener = (evt: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(evt.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(evt.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, []);

  /* Sync external value */
  useEffect(() => setSearchTerm(value), [value]);

  /* Cleanup debounce on unmount */
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  /*  Styling helpers */
  const sizeCls =
    size === "sm"
      ? "h-8 px-2.5 py-1 text-[13px] rounded-md"
      : "px-3 py-2 rounded-lg";
  const base = `w-full ${sizeCls} border focus:ring-2 focus:border-transparent placeholder-gray-400`;
  const errorCls = error
    ? "border-red-300 focus:ring-red-500"
    : "border-gray-300 focus:ring-primary";
  const disCls = disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "";

  const Icon = iconForField[searchField];

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        className={`${base} ${errorCls} ${disCls} ${className}`}
        autoComplete="off"
      />

      {localLoading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="animate-spin h-4 w-4 rounded-full border-b-2 border-primary-600" />
        </div>
      )}

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg"
        >
          {suggestions.length > 0
            ? suggestions.map((carrier) => {
                return (
                  <div
                    key={carrier._id}
                    onClick={() => handleCarrierSelect(carrier)}
                    className="cursor-pointer border-b border-gray-100 px-4 py-3 last:border-b-0 hover:bg-gray-50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 truncate">
                        <Icon className="h-4 w-4 text-gray-400 shrink-0" />
                        <span className="truncate font-medium text-gray-900">
                          {vehicleLabel(carrier)}
                        </span>
                      </div>
                      {(vehiclePlateHint(carrier) || carrier.markAndModel) && (
                        <span className="shrink-0 text-xs text-gray-500">
                          {[vehiclePlateHint(carrier), carrier.markAndModel]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            : /* Empty‑state */
              !localLoading &&
              searchTerm.length >= 2 && (
                <div className="p-4 text-center text-gray-500">
                  {t("filters.noResults")} "{searchTerm}"
                </div>
              )}
        </div>
      )}
    </div>
  );
};
